import {
  createOrganizationRequestSchema,
  createOrganizationResponseSchema,
  commonErrorCodeResponseSchema,
  organizationUpdateRequestSchema,
  organizationUpdateResponseSchema,
  organizationResponseSchema,
  organizationGetMembersResponseSchema,
  getInvitationPublicResponseSchema,
  acceptInvitationResponseSchema,
  acceptInvitationRequestSchema,
  createInvitationDtoSchema,
  createInvitationResponseSchema,
  existingOrganizationMemberResponseSchema,
  deleteResponseSchema,
  successResponseSchema,
  invitationResendThrottleErrorResponseSchema,
  unauthorizedResponseSchema,
  badRequestResponseSchema,
  permissionDeniedResponseSchema,
  notFoundResponseSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Extend request objects only for the frontend with necessary path params
const CRUD = {
  r: z.object({ orgId: z.string() }),
  i: z.object({ id: z.string() }),
};

const endpoints = {
  'organization.create': {
    method: 'POST',
    url: `${API_URL_BASE}organizations`,
    requestSchema: createOrganizationRequestSchema,
    responseSuccessSchema: createOrganizationResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
      401: unauthorizedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof createOrganizationRequestSchema,
    typeof createOrganizationResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
      401: typeof unauthorizedResponseSchema;
    }
  >,
  'organization.me': {
    method: 'GET',
    url: `${API_URL_BASE}organization/me`,
    requestSchema: z.undefined(),
    responseSuccessSchema: organizationResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof organizationResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'organization.members': {
    method: 'GET',
    url: `${API_URL_BASE}organizations/:orgId/members`,
    requestSchema: CRUD.r,
    responseSuccessSchema: organizationGetMembersResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof organizationGetMembersResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'organization.members.create': {
    method: 'POST',
    url: `${API_URL_BASE}invitations`,
    requestSchema: createInvitationDtoSchema,
    responseSuccessSchema: createInvitationResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
      409: existingOrganizationMemberResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof createInvitationDtoSchema,
    typeof createInvitationResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
      409: typeof existingOrganizationMemberResponseSchema;
    }
  >,
  'organization.update': {
    method: 'PUT',
    url: `${API_URL_BASE}organizations`,
    requestSchema: organizationUpdateRequestSchema,
    responseSuccessSchema: organizationUpdateResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof organizationUpdateRequestSchema,
    typeof organizationUpdateResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'organization.invitation.get': {
    method: 'GET',
    url: `${API_URL_BASE}invitations/public/:id`,
    requestSchema: CRUD.i,
    responseSuccessSchema: getInvitationPublicResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.i,
    typeof getInvitationPublicResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'organization.invitation.accept': {
    method: 'POST',
    url: `${API_URL_BASE}auth/accept-invitation`,
    requestSchema: acceptInvitationRequestSchema,
    responseSuccessSchema: acceptInvitationResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof acceptInvitationRequestSchema,
    typeof acceptInvitationResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'organization.invitation.delete': {
    method: 'DELETE',
    url: `${API_URL_BASE}invitations/:id`,
    requestSchema: CRUD.i,
    responseSuccessSchema: deleteResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.i,
    typeof deleteResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'organization.invitation.resend': {
    method: 'POST',
    url: `${API_URL_BASE}invitations/:id/resend`,
    requestSchema: CRUD.i,
    responseSuccessSchema: successResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      429: invitationResendThrottleErrorResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.i,
    typeof successResponseSchema,
    {
      429: typeof invitationResendThrottleErrorResponseSchema;
    }
  >,
} as const;
export default endpoints;
